import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileSearch } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";

export default function AuditLog() {
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    api.get("/cases").then((r) => {
      setCases(r.data);
      if (r.data[0]) setSelected(r.data[0].case_id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/cases/${selected}`).then((r) => setEntries(r.data.audit || []));
  }, [selected]);

  const currentCase = cases.find((c) => c.case_id === selected);

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-[1400px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Court-Ready Audit Trail</div>
            <h1 className="font-serif text-4xl tracking-tight font-medium" data-testid="audit-title">Immutable activity log</h1>
          </div>
          {currentCase && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.case_id}/audit/export`}
              target="_blank"
              rel="noreferrer"
              className="fa-btn-primary"
              data-testid="audit-export-pdf"
            >
              <ShieldCheck className="w-4 h-4" /> Export to PDF
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 fa-card">
            <div className="text-xs uppercase tracking-widest text-slate-500 p-4 border-b border-slate-200">Select case</div>
            <div className="max-h-[600px] overflow-auto divider-y">
              {cases.map((c) => (
                <button
                  key={c.case_id}
                  data-testid={`audit-case-${c.case_id}`}
                  onClick={() => setSelected(c.case_id)}
                  className={`w-full text-left p-4 hover:bg-slate-50 ${selected === c.case_id ? "bg-[#0B1B3D]/5 border-l-2 border-l-[#0B1B3D]" : ""}`}
                >
                  <div className="flex items-center gap-2 font-mono text-xs text-slate-500 mb-1">
                    <FileSearch className="w-3 h-3" /> {c.case_number}
                  </div>
                  <div className="text-sm truncate">{c.subject}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="fa-card">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Log entries</div>
                  {currentCase && <div className="text-sm font-semibold mt-0.5">{currentCase.subject}</div>}
                </div>
                <div className="font-mono text-xs text-slate-500">{entries.length} entries</div>
              </div>
              {entries.length === 0 ? (
                <div className="p-12 text-center text-slate-500 text-sm">No entries.</div>
              ) : (
                <div className="divider-y" data-testid="audit-entries">
                  {entries.map((e) => (
                    <div key={e.entry_id} className="p-4 grid grid-cols-12 gap-4">
                      <div className="col-span-3 font-mono text-xs text-slate-500">{new Date(e.timestamp).toLocaleString()}</div>
                      <div className="col-span-2 text-xs">{e.actor_name}</div>
                      <div className="col-span-3 font-mono text-xs"><span className="bg-slate-100 px-1.5 py-0.5">{e.action}</span></div>
                      <div className="col-span-4 text-xs text-slate-600 font-mono break-all">{JSON.stringify(e.details)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-3 font-mono">
              ENTRIES ARE APPEND-ONLY. Tampering would break the cryptographic chain (V2: hash chain in production).
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
